"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { CASE_STUDIES } from "@/lib/data/case-studies";
import { HeroSection } from "@/components/ui/HeroSection";
import { Section, Typography } from "../ui";

type Category =
  | "all"
  | "corporate"
  | "retail"
  | "restaurant"
  | "transportation"
  | "education"
  | "banking"
  | "others";

// data now imported from lib/data/case-studies

const FILTERS: { key: Category; label: string }[] = [
  { key: "all", label: "All" },
  { key: "corporate", label: "Corporate" },
  { key: "retail", label: "Retail" },
  { key: "restaurant", label: "Restaurant" },
  { key: "transportation", label: "Transportation" },
  { key: "education", label: "Education" },
  { key: "banking", label: "Banking & Finance" },
  { key: "others", label: "Others" },
];

export default function CaseStudiesIndexClient() {
  const [activeFilter, setActiveFilter] = useState<Category>("all");

  return (
    <>
      {/* Hero Section */}
      <HeroSection
        title="Case Studies"
        description={
          <>
            Explore our client&apos;s success stories and discover our potential in accelerating
            your organization&apos;s success. And choose Pickcel&apos;s{" "}
            <Link
              className="text-primary underline hover:text-accent hover:underline"
              href="/"
            >
              digital signage solutions
            </Link>{" "}
            to create your own success story.
          </>
        }
        buttons={[
          {
            text: "Start Free Trial",
            href: "https://console.pickcel.com/auth/register",
            variant: "primary",
            arrow: "grow",
          },
        ]}
        showBackgroundGradient={false}
      />

      {/* Filters + Cards Grid */}
      <Section className="pt-0">
        <div role="tablist" aria-label="Filter case studies by category">
          <ul className="flex flex-wrap gap-2 justify-center mb-12">
            {FILTERS.map(f => (
              <li key={f.key}>
                <button
                  id={`tab-${f.key}`}
                  type="button"
                  role="tab"
                  aria-selected={activeFilter === f.key}
                  aria-controls={`panel-${f.key}`}
                  onClick={() => setActiveFilter(f.key)}
                  className={
                    `rounded-md border text-sm font-medium transition-colors py-[0.438rem] px-[1.125rem] ` +
                    (activeFilter === f.key
                      ? "bg-secondary/10 shadow-md border-secondary/20 text-secondary"
                      : "bg-white border-gray-300 text-text-primary")
                  }
                >
                  <span className="text-[12px] font-medium">{f.label}</span>
                </button>
              </li>
            ))}
          </ul>
          {FILTERS.map(filter => {
            const isActive = activeFilter === filter.key;
            const cardsToShow =
              filter.key === "all"
                ? CASE_STUDIES
                : CASE_STUDIES.filter(
                    c => String(c.category).toLowerCase() === String(filter.key).toLowerCase()
                  );

            return (
              <div
                key={filter.key}
                className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 ${
                  isActive ? "block" : "hidden"
                }`}
                id={`panel-${filter.key}`}
                role="tabpanel"
                aria-labelledby={`tab-${filter.key}`}
                aria-hidden={!isActive}
              >
                {cardsToShow.map((card, index) => (
                  <div
                    key={card.href}
                    className="overflow-hidden rounded-xl transition-all duration-300"
                  >
                    <Link
                      href={card.href}
                      className="h-full rounded-xl"
                      aria-label={`${card.title} case study`}
                    >
                      <div className="overflow-hidden rounded-lg">
                        <Image
                          src={card.image.src}
                          alt={card.image.alt}
                          title={card.image.alt}
                          width={285}
                          height={256}
                          quality={75}
                          loading={index < 3 ? "eager" : "lazy"}
                          priority={index < 3}
                          fetchPriority={index < 3 ? "high" : undefined}
                          className="w-full h-auto sm:h-36 object-cover transform transition-all duration-300 hover:scale-105"
                        />
                      </div>
                      <div className="flex py-4 items-center justify-between">
                        <Typography variant="h4" as="h3" className="group-hover:text-secondary">{card.title}</Typography>
                        <span className="section-label">
                          {card.category}
                        </span>
                      </div>
                      <Typography variant="small" as="p" className="pb-4">{card.description}</Typography>
                    </Link>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </Section>
    </>
  );
}
